'use client';

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { Timeframe, SportFilter, BetTypeFilter } from './ReportingFilters';

interface SportRow {
  sport: string;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number;
  totalUnits: number;
  totalRisked: number;
  roi: number;
}

interface Props {
  timeframe: Timeframe;
  sport: SportFilter;
  betType: BetTypeFilter;
}

function getDateFilter(timeframe: Timeframe): string | null {
  const now = new Date();
  if (timeframe === '7d') { now.setDate(now.getDate() - 7); return now.toISOString(); }
  if (timeframe === '30d') { now.setDate(now.getDate() - 30); return now.toISOString(); }
  if (timeframe === '90d') { now.setDate(now.getDate() - 90); return now.toISOString(); }
  if (timeframe === 'ytd') { return new Date(now.getFullYear(), 0, 1).toISOString(); }
  return null;
}

export default function SportBreakdownTable({ timeframe, sport, betType }: Props) {
  const [rows, setRows] = useState<SportRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        let query = supabase
          .from('picks')
          .select('sport, result, units, units_result, posted_at')
          .neq('result', 'pending');

        if (sport !== 'all') query = query.eq('sport', sport);
        if (betType !== 'all') query = query.eq('bet_type', betType);
        const dateFrom = getDateFilter(timeframe);
        if (dateFrom) query = query.gte('posted_at', dateFrom);

        const { data, error } = await query;
        if (error) throw error;

        const bySport: Record<string, { wins: number; losses: number; pushes: number; units: number; risked: number }> = {};
        (data || []).forEach((p) => {
          if (!bySport[p.sport]) bySport[p.sport] = { wins: 0, losses: 0, pushes: 0, units: 0, risked: 0 };
          bySport[p.sport].units += p.units_result || 0;
          bySport[p.sport].risked += p.units || 0;
          if (p.result === 'win') bySport[p.sport].wins++;
          else if (p.result === 'loss') bySport[p.sport].losses++;
          else if (p.result === 'push') bySport[p.sport].pushes++;
        });

        const stats: SportRow[] = Object.entries(bySport).map(([s, d]) => {
          const decidedGames = d.wins + d.losses;
          const winRate = decidedGames > 0 ? parseFloat(((d.wins / decidedGames) * 100).toFixed(1)) : 0;
          const roi = d.risked > 0 ? parseFloat(((d.units / d.risked) * 100).toFixed(1)) : 0;
          return {
            sport: s,
            wins: d.wins,
            losses: d.losses,
            pushes: d.pushes,
            winRate,
            totalUnits: parseFloat(d.units.toFixed(2)),
            totalRisked: parseFloat(d.risked.toFixed(1)),
            roi,
          };
        });

        // Sort by ROI descending
        setRows(stats.sort((a, b) => b.roi - a.roi));
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [timeframe, sport, betType]);

  return (
    <div className="stat-card mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-[hsl(var(--foreground))]">Sport Performance Breakdown</h2>
          <p className="text-xs text-[hsl(var(--foreground-muted))] mt-0.5">
            Cumulative ROI % and win rate by sport — sorted by strongest ROI
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[120px] gap-2">
          <Loader2 size={18} className="animate-spin text-[hsl(var(--primary))]" />
          <span className="text-sm text-[hsl(var(--foreground-muted))]">Loading...</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex items-center justify-center h-[120px]">
          <p className="text-sm text-[hsl(var(--foreground-muted))]">No graded picks yet. Data will appear once picks are graded.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[hsl(var(--border))]">
                <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--foreground-muted))]">Sport</th>
                <th className="text-center py-2.5 px-3 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--foreground-muted))]">Record</th>
                <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--foreground-muted))]">Win Rate</th>
                <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--foreground-muted))]">Net Units</th>
                <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--foreground-muted))]">ROI %</th>
                <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--foreground-muted))]">Trend</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const isPositiveRoi = row.roi >= 0;
                const isPositiveUnits = row.totalUnits >= 0;
                const winRateGood = row.winRate >= 52.4;
                return (
                  <tr
                    key={row.sport}
                    className={`border-b border-[hsl(var(--border)/0.5)] hover:bg-[hsl(var(--card-elevated))] transition-colors ${i === 0 ? 'bg-[hsl(var(--primary)/0.03)]' : ''}`}
                  >
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        {i === 0 && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.1)] border border-[hsl(var(--primary)/0.2)] px-1.5 py-0.5 rounded-full">
                            #1
                          </span>
                        )}
                        <span className="font-semibold text-[hsl(var(--foreground))]">{row.sport}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className="font-mono text-xs text-[hsl(var(--foreground-muted))]">
                        {row.wins}-{row.losses}{row.pushes > 0 ? `-${row.pushes}` : ''}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <span className={`font-mono font-bold tabular-nums text-sm ${winRateGood ? 'text-[hsl(var(--primary))]' : 'text-amber-400'}`}>
                        {row.winRate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <span className={`font-mono font-bold tabular-nums text-sm ${isPositiveUnits ? 'text-[hsl(var(--primary))]' : 'text-red-400'}`}>
                        {isPositiveUnits ? '+' : ''}{row.totalUnits.toFixed(2)}u
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <span
                        className={`inline-flex items-center justify-end font-mono font-bold tabular-nums text-sm px-2 py-0.5 rounded-lg ${
                          isPositiveRoi
                            ? 'text-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.1)]'
                            : 'text-red-400 bg-red-500/10'
                        }`}
                      >
                        {isPositiveRoi ? '+' : ''}{row.roi.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      {isPositiveRoi ? (
                        <TrendingUp size={15} className="text-[hsl(var(--primary))] ml-auto" />
                      ) : row.roi === 0 ? (
                        <Minus size={15} className="text-[hsl(var(--foreground-muted))] ml-auto" />
                      ) : (
                        <TrendingDown size={15} className="text-red-400 ml-auto" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
